import { pemDecodeAll, pemEncode } from '../bytes/encoding';
import { ParseError } from '../bytes/errors';
import type { LogTransport } from '../transport/logTransport';
import { parseCertificate } from '../x509/parse';
import type { DiscoveredChain } from './types';

export const WMCC_GENERATE = 'https://web.api.sslmate.com/whatsmychaincert/generate';
/** Pseudo log id used for reachability bookkeeping. */
export const WMCC_ID = 'whatsmychaincert';

/**
 * SSLMate's "What's My Chain Cert?" API builds the correct chain for a certificate or
 * precertificate from its database of CA certificates. It sends `Access-Control-Allow-Origin: *`
 * and works with a plain GET, so it is usable straight from a web page — no proxy, no AIA.
 */
export async function chainFromWhatsMyChainCert(certDer: Uint8Array, transport: LogTransport, signal?: AbortSignal): Promise<DiscoveredChain> {
  const q = new URLSearchParams({ cert_pem: pemEncode(certDer), include_leaf: '1', include_root: '1' });
  const { text } = await transport.text({ id: WMCC_ID }, 'issuer', `${WMCC_GENERATE}?${q}`, signal ? { signal } : undefined);
  // The API answers errors as plain text; only PEM blocks count.
  const chain = text.includes('-----BEGIN') ? pemDecodeAll(text).filter((b) => b.label === 'CERTIFICATE').map((b) => b.der) : [];
  if (chain.length === 0) throw new ParseError(`whatsmychaincert: no certificates in the response (${text.slice(0, 120).replace(/\s+/g, ' ')})`);
  if (chain[0]!.toHex() !== certDer.toHex()) chain.unshift(certDer);
  const parsed = chain.map((d) => parseCertificate(d));
  const last = parsed[parsed.length - 1]!;
  return { chain, subjects: parsed.map((p) => p.subject), endsAtRoot: last.subject === last.issuer, source: 'whatsmychaincert', detail: 'whatsmychaincert.com (SSLMate)' };
}

/**
 * Retrieves a live server's certificate and builds its chain, via the same API's `host=` form
 * (`example.com` or `example.com:8443`). Lets the explorer inspect or submit a site's current
 * certificate without openssl on the user's side.
 */
export async function chainFromHost(host: string, transport: LogTransport, signal?: AbortSignal): Promise<DiscoveredChain> {
  const h = host.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (!/^[A-Za-z0-9.-]+(:\d{1,5})?$/.test(h)) throw new ParseError(`"${host}" is not a hostname (use host or host:port)`);
  const q = new URLSearchParams({ host: h, include_leaf: '1', include_root: '1' });
  const { text } = await transport.text({ id: WMCC_ID }, 'issuer', `${WMCC_GENERATE}?${q}`, signal ? { signal } : undefined);
  const chain = text.includes('-----BEGIN') ? pemDecodeAll(text).filter((b) => b.label === 'CERTIFICATE').map((b) => b.der) : [];
  if (chain.length === 0) throw new ParseError(`whatsmychaincert: no certificates for ${h} (${text.slice(0, 120).replace(/\s+/g, ' ')})`);
  const parsed = chain.map((d) => parseCertificate(d));
  const last = parsed[parsed.length - 1]!;
  return { chain, subjects: parsed.map((p) => p.subject), endsAtRoot: last.subject === last.issuer, source: 'whatsmychaincert', detail: `whatsmychaincert.com (SSLMate), certificate served by ${h}` };
}
